import QRCode from 'qrcode';
import jsQR from 'jsqr';

export const QR_PALETTES = Object.freeze({
  mono: Object.freeze({ dark: '#000000', light: '#ffffff', label: 'Noir / blanc' }),
  cyan: Object.freeze({ dark: '#00151a', light: '#d8ffff', label: 'Cyan contrasté' }),
  amber: Object.freeze({ dark: '#170b00', light: '#fff1c2', label: 'Ambre contrasté' }),
});

export function createQrMatrix(text, {
  errorCorrectionLevel = 'H',
  version,
  maskPattern,
} = {}) {
  const qr = QRCode.create(text, { errorCorrectionLevel, version, maskPattern });
  return { size: qr.modules.size, data: Uint8Array.from(qr.modules.data), version: qr.version };
}

export function renderQrRgba(text, {
  errorCorrectionLevel = 'H',
  scale = 8,
  margin = 4,
  dark = '#000000',
  light = '#ffffff',
} = {}) {
  const matrix = createQrMatrix(text, { errorCorrectionLevel });
  const darkRgb = parseHexColor(dark);
  const lightRgb = parseHexColor(light);
  const dimension = (matrix.size + margin * 2) * scale;
  const rgba = new Uint8ClampedArray(dimension * dimension * 4);
  for (let y = 0; y < dimension; y++) {
    const moduleY = Math.floor(y / scale) - margin;
    for (let x = 0; x < dimension; x++) {
      const moduleX = Math.floor(x / scale) - margin;
      const on = moduleX >= 0 && moduleY >= 0 && moduleX < matrix.size && moduleY < matrix.size
        ? matrix.data[moduleY * matrix.size + moduleX] === 1
        : false;
      const color = on ? darkRgb : lightRgb;
      const offset = (y * dimension + x) * 4;
      rgba[offset] = color[0];
      rgba[offset + 1] = color[1];
      rgba[offset + 2] = color[2];
      rgba[offset + 3] = 255;
    }
  }
  return { data: rgba, width: dimension, height: dimension, matrixSize: matrix.size, version: matrix.version };
}

export function drawQrCanvas(canvas, text, options = {}) {
  const palette = QR_PALETTES[options.palette] || QR_PALETTES.mono;
  const rendered = renderQrRgba(text, { ...options, dark: palette.dark, light: palette.light });
  canvas.width = rendered.width;
  canvas.height = rendered.height;
  const context = canvas.getContext('2d', { alpha: false });
  const image = context.createImageData(rendered.width, rendered.height);
  image.data.set(rendered.data);
  context.putImageData(image, 0, 0);
  return rendered;
}

export function decodeQrRgba(data, width, height) {
  const result = jsQR(data, width, height, { inversionAttempts: 'attemptBoth' });
  if (!result) return null;
  return {
    text: result.data,
    binaryData: result.binaryData,
    version: result.version,
    location: result.location,
  };
}

export function decodeCanvas(canvas, context = canvas.getContext('2d', { willReadFrequently: true })) {
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  return decodeQrRgba(image.data, image.width, image.height);
}

function parseHexColor(value) {
  if (!/^#[0-9a-f]{6}$/iu.test(value)) throw new TypeError(`couleur hexadécimale invalide: ${value}`);
  return [1, 3, 5].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
}
